import * as React from "react";
import { Box, Button, Card, Link, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CampaignBrief, campaignBriefsKeys } from "@/hooks/api/use-campaign-briefs";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { CustomTable } from "@/components/customTable";
import { Column } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { BriefsCalendar } from "../client-requests/calendar";
import { clocksFor, formatDate, StatusChip } from "./components";
import { regionLabel } from "./constants";

/**
 * The table on its own, so its query only runs when the list is showing: the table supplies the
 * paging parameters, and without it mounted the request goes out without them and is refused.
 */
function CampaignBriefsList(): React.JSX.Element {
	const navigate = useNavigate();

	const {
		items: briefs,
		isLoading,
		totalItems,
		refetchData,
	} = useCachedTableStateWithUrl<CampaignBrief>({
		queryKey: campaignBriefsKeys.lists(),
		endpoint: "/campaign-briefs",
	});

	const columns: Column[] = React.useMemo(
		() => [
			{
				header: { id: "title", label: "Campaign", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "title",
				getCellValue: (row: CampaignBrief) => (
					<Link component="button" variant="subtitle2" onClick={() => navigate(`/campaign-briefs/${row.id}`)}>
						{row.title}
					</Link>
				),
			},
			{
				header: { id: "dial", label: "First dial", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "dialDate",
				getCellValue: (row: CampaignBrief) => (
					<Box>
						<Typography variant="body2">{formatDate(row.dialDate)}</Typography>
						<Typography variant="caption" color="text.secondary">
							{clocksFor(row.regions)}
						</Typography>
					</Box>
				),
			},
			{
				header: { id: "regions", label: "Regions", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "regions",
				getCellValue: (row: CampaignBrief) => row.regions.map((code) => regionLabel(code)).join(", "),
			},
			{
				header: { id: "status", label: "Status", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "status",
				getCellValue: (row: CampaignBrief) => <StatusChip status={row.status} />,
			},
			{
				header: { id: "submitted", label: "Submitted", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "submittedAt",
				getCellValue: (row: CampaignBrief) =>
					row.submittedAt ? (
						<Box>
							<Typography variant="body2">{dayjs(row.submittedAt).format("D MMM YYYY")}</Typography>
							<Typography variant="caption" color="text.secondary">
								{row.submittedByName ?? ""}
							</Typography>
						</Box>
					) : (
						"-"
					),
			},
		],
		[navigate]
	);

	return (
		<TableAnimation delay={0}>
			<Card>
				<CustomTable
					columns={columns}
					data={briefs}
					refetchData={refetchData}
					isLoading={isLoading}
					totalItems={totalItems}
				/>
			</Card>
		</TableAnimation>
	);
}

/** The account's campaign requests, as a list or a calendar, and the button that starts a new one. */
export function CampaignBriefs(): React.JSX.Element {
	const navigate = useNavigate();
	// In the URL, so going back from a request returns to the calendar rather than the list.
	const [searchParams, setSearchParams] = useSearchParams();
	const view = searchParams.get("view") === "calendar" ? "calendar" : "list";
	const showView = (next: "list" | "calendar") => {
		const params = new URLSearchParams(searchParams);
		if (next === "calendar") params.set("view", "calendar");
		else params.delete("view");
		setSearchParams(params, { replace: true });
	};

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={3}
					sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
				>
					<Box sx={{ flex: "1 1 auto" }}>
						<Typography variant="h4">Campaign requests</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							Ask us to set up a campaign. We’ll review it and keep the status here up to date.
						</Typography>
					</Box>
					<Stack direction="row" spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
						<ToggleButtonGroup
							exclusive
							size="small"
							value={view}
							onChange={(_, next: "list" | "calendar" | null) => next && showView(next)}
							aria-label="View"
						>
							<ToggleButton value="list">List</ToggleButton>
							<ToggleButton value="calendar">Calendar</ToggleButton>
						</ToggleButtonGroup>
						<Button
							startIcon={<PlusIcon />}
							variant="contained"
							onClick={() => navigate("/campaign-briefs/new")}
							sx={{ whiteSpace: "nowrap" }}
						>
							New campaign request
						</Button>
					</Stack>
				</Stack>

				{view === "calendar" ? <BriefsCalendar scope="client" /> : <CampaignBriefsList />}
			</Stack>
		</Box>
	);
}
